import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import toast from 'react-hot-toast';

const ProfileModal = ({ isOpen, onClose, user, onSave }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    location: '',
    bio: ''
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || user.name?.split(' ')[0] || '',
        lastName: user.lastName || user.name?.split(' ').slice(1).join(' ') || '',
        email: user.email || '',
        phone: user.phone || '',
        location: user.location || '',
        bio: user.bio || ''
      });
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      await onSave(formData);
      toast.success('Profile updated successfully!');
      onClose();
    } catch (error) {
      console.error('Failed to save profile:', error);
      toast.error(`Failed to update profile: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen || !user) return null;

  const initial = (user.firstName || user.name || '?')?.charAt(0).toUpperCase();
  const displayName = `${formData.firstName} ${formData.lastName}`.trim() || user.name || 'Your name';
  const email = formData.email || user.email;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-40 backdrop-blur-sm">
      <div className="bg-white rounded-lg border border-gray-200 ring-1 ring-cyan-400/60 shadow-lg w-full max-w-md overflow-hidden">
        <form onSubmit={handleSubmit}>
          {/* Header */}
          <div className="flex items-start justify-between px-4 pt-4 pb-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0 bg-gray-100">
                {user.photo ? (
                  <img src={user.photo} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-blue-600 font-bold text-sm">{initial}</div>
                )}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-800 truncate">{displayName}</p>
                <p className="text-xs text-gray-400 truncate">{email}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 rounded-full p-1 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="border-b border-gray-100" />

          {/* Fields */}
          <div className="px-4 divide-y divide-gray-100">
            <FieldRow label="First Name" name="firstName" value={formData.firstName} onChange={handleChange} />
            <FieldRow label="Last Name" name="lastName" value={formData.lastName} onChange={handleChange} />
            <FieldRow label="Email account" name="email" value={formData.email} readOnly />
            <FieldRow label="Mobile number" name="phone" value={formData.phone} onChange={handleChange} placeholder="Add number" />
            <FieldRow label="Location" name="location" value={formData.location} onChange={handleChange} placeholder="Add location" />

            {/* Description */}
            <div className="py-2.5">
              <label className="block text-xs font-medium text-gray-400 mb-1">Description</label>
              <textarea
                name="bio"
                value={formData.bio}
                onChange={handleChange}
                rows="2"
                placeholder="Tell us a little about yourself..."
                className="w-full text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded px-2.5 py-1.5 outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 resize-none"
              />
            </div>
          </div>

          {/* Save */}
          <div className="px-4 py-3 flex justify-start">
            <button
              type="submit"
              disabled={isSaving}
              className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded px-5 h-7 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSaving ? 'Saving...' : 'Save Change'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const FieldRow = ({ label, name, value, onChange, placeholder, readOnly }) => (
  <div className="flex items-center justify-between gap-4 py-2.5">
    <label className="flex-shrink-0 text-xs font-medium text-gray-400">{label}</label>
    <input
      type="text"
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      readOnly={readOnly}
      className="flex-1 min-w-0 text-right text-sm text-gray-700 bg-transparent outline-none focus:text-blue-600 py-0 px-0"
    />
  </div>
);

export default ProfileModal;